/* eslint-disable no-param-reassign, react/prop-types */
import { DreamBlock } from '@typedream/core';
import {
  deselect,
  findNodePath,
  getBlockAbove,
  getEndPoint,
  getNode,
  getStartPoint,
  isCollapsed,
  isEditorFocused,
  isElement,
  isEndPoint,
  isExpanded,
  isStartPoint,
  isText,
  select,
  TNodeEntry,
  toSlateNode,
} from '@udecode/plate-core';
import { SyntheticEvent } from 'react';
import { Path } from 'slate';
import { isBlockEmpty, isTextBlock } from '../../editorTools/queries';
import { createTPluginFactory, ElementPluginOptions, getTPlugins } from '../../plate';
import { KEY_ELEMENT_SELECTION } from '../../pluginKeys';
import { DreamEditor } from '../../types';
import { deselectElements, selectElement } from './transforms';
import { withElementSelection } from './withElementSelection';
import { isSelectAllHotkey } from '../../hotkeys';

const getElementEntriesFromEvent = (editor: DreamEditor, event: SyntheticEvent) => {
  const slateNode = toSlateNode(editor, event.target as Node) as DreamBlock;

  if (!slateNode) return {};

  const path = findNodePath(editor, slateNode);

  if (!path) return {};

  const selectableElementTypes = getTPlugins(editor)
    .filter((p) => p.isElement && (p.options as ElementPluginOptions)?.isSelectable)
    .map((p) => p.type);

  const targetEntry: TNodeEntry<DreamBlock> = [slateNode, path];
  let selectableEntry: typeof targetEntry | undefined = targetEntry;

  if (!isElement(slateNode) || !selectableElementTypes.includes(slateNode.type)) {
    selectableEntry = getBlockAbove(editor, {
      match: (n: DreamBlock) => selectableElementTypes.includes(n.type),
      at: findNodePath(editor, slateNode),
    });
  }

  return { target: targetEntry, selectable: selectableEntry };
};

export const createElementSelectionPlugin = createTPluginFactory({
  key: KEY_ELEMENT_SELECTION,
  withOverrides: withElementSelection,
  handlers: {
    onMouseUp: (editor) => (event) => {
      if (!isEditorFocused(editor)) return;

      const { target, selectable } = getElementEntriesFromEvent(editor, event);

      if (!target) return;

      // deselect text when selecting an element block
      if (!isText(target[0]) && !isTextBlock(editor, target[0]) && isCollapsed(editor.selection)) {
        deselect(editor);
      }

      if (selectable?.[1]) {
        selectElement(editor, selectable[1], !!event.metaKey);
      } else if (!event.metaKey) {
        deselectElements(editor);
      }
    },

    onMouseOver: (editor) => (event) => {
      if (!isEditorFocused(editor)) return;

      const { selectable } = getElementEntriesFromEvent(editor, event);

      if (selectable) {
        const currentHoveredPath = editor.store.getState().hoveredElementPath;
        if (!currentHoveredPath || !Path.equals(selectable[1], currentHoveredPath)) {
          editor.store.setState({ hoveredElementPath: selectable[1] });
        }
      } else {
        editor.store.setState({ hoveredElementPath: null });
      }
    },

    // this onKeyDown is ported from `onKeyDownSelectAll` in
    // legacy editor.
    onKeyDown: (editor) => (event) => {
      if (!isEditorFocused(editor) || !editor.selection) return;

      if (isSelectAllHotkey(event)) {
        event.preventDefault();
        // Check if selection expanded (meaning start and end are different)
        const expanded = isExpanded(editor.selection);
        // Get path of current block
        const path = getBlockAbove(editor)?.[1] || [];
        try {
          if (editor.selection?.anchor) {
            // anchor and focus are interchangeable, depending whether
            // selecting from left to right or right to left
            const selectedFromStart =
              isStartPoint(editor, editor.selection.anchor, path) ||
              isStartPoint(editor, editor.selection.focus, path);
            const selectedToEnd =
              isEndPoint(editor, editor.selection.anchor, path) ||
              isEndPoint(editor, editor.selection.focus, path);
            // if entire block is already selected, or current block is empty, then select entire document
            const element = getNode(editor, path);

            if ((expanded && selectedFromStart && selectedToEnd) || isBlockEmpty(editor, element)) {
              select(editor, {
                anchor: getStartPoint(editor, [0]),
                focus: getEndPoint(editor, [0]),
              });
            } else {
              select(editor, path);
            }
          }
        } catch (e) {
          select(editor, path);
        }
      }
    },
  },
});
